// RewardsTab.jsx — updated
// Removed: daily code management
// Added: staff PIN setter, testMode toggle
// QR2 rotates daily automatically — owner only rotates manually for security

import { useState, useEffect } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const C = {
  deepBlue:  "#1a3a5c",
  midBlue:   "#2563a8",
  lightBlue: "#dbeafe",
  cream:     "#fdf6e3",
  saffron:   "#f5a623",
  white:     "#ffffff",
  textDark:  "#0f2138",
  textMid:   "#4a6580",
  textLight: "#8faec8",
  success:   "#16a34a",
  error:     "#dc2626",
  border:    "#c8ddf0",
};

const BUSINESS_ID = "biz-a63374ba-df71-48d5-b419-3447eedd6c8f";
const SLUG = "milkas";

function todayIST() {
  return new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  ).toISOString().slice(0, 10);
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function RewardsTab({ businessId = BUSINESS_ID }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [members, setMembers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // QR2 — URL always fetched from backend server-side, never generated client-side
  const [qr2Url, setQr2Url] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [offerText, setOfferText] = useState("");
  const [offerHindi, setOfferHindi] = useState("");
  const [offerExpiry, setOfferExpiry] = useState("today");
  const [offerCustomHours, setOfferCustomHours] = useState("4");
  const [postingOffer, setPostingOffer] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [pushCount, setPushCount] = useState(null);

  // Staff PIN
  const [staffPin, setStaffPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  // Test mode
  const [testMode, setTestMode] = useState(
    localStorage.getItem(`testmode_${businessId}`) !== "false"
  );
  const [savingTestMode, setSavingTestMode] = useState(false);

  // Adjust
  const [adjustPhone, setAdjustPhone] = useState("");
  const [adjustPoints, setAdjustPoints] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [suspects, setSuspects] = useState([]);
  const [blocking, setBlocking] = useState(null); // memberId being blocked

  useEffect(() => { fetchAll(); }, [businessId]);

  async function fetchQr2Url() {
    try {
      // Owner-only route — returns QR2 URL using businessId only, no staff PIN
      const r = await fetch(`${API}/rewards/admin/qr2url?businessId=${businessId}`);
      if (r.ok) {
        const d = await r.json();
        setQr2Url(d.qr2Url);
      }
    } catch { /* silent */ }
  }

  async function fetchAll() {
    setLoading(true);
    try {
      const [mRes, rRes, cRes, sRes] = await Promise.all([
        fetch(`${API}/rewards/leaderboard?businessId=${businessId}`),
        fetch(`${API}/rewards/admin/redemptions?businessId=${businessId}`),
        fetch(`${API}/rewards/config?businessId=${businessId}`),
        fetch(`${API}/rewards/admin/suspects?businessId=${businessId}`),
      ]);
      if (mRes.ok) { const d = await mRes.json(); setMembers(d.members || []); }
      if (rRes.ok) { const d = await rRes.json(); setRedemptions(d.redemptions || []); }
      if (sRes.ok) { const d = await sRes.json(); setSuspects(d.suspects || []); }
      // Fetch active offer from config
      const cRes2 = await fetch(`${API}/rewards/config?businessId=${businessId}`);
      if (cRes2.ok) { const d = await cRes2.json(); setActiveOffer(d.todayOffer || null); }
      // Note: public config doesn't return testMode — we read it from DynamoDB
      // testMode state stays as seeded default (true) unless owner toggles it
    } catch { showToast("Failed to load data", "error"); }
    finally { setLoading(false); }
  }

  async function handleRotateQR2() {
    if (!window.confirm(
      "Rotate QR 2?\n\nThe current QR stops working immediately.\nStaff dashboard will show new QR automatically.\n\nOnly do this if you suspect fraud.\n\nContinue?"
    )) return;
    setRotating(true);
    try {
      // Generate new secret browser-side and send to backend
      const arr = new Uint8Array(24);
      crypto.getRandomValues(arr);
      const newSecret = Array.from(arr).map(b => b.toString(16).padStart(2,"0")).join("");
      const r = await fetch(`${API}/rewards/admin/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, updates: { qr2Secret: newSecret } }),
      });
      if (!r.ok) { const d = await r.json(); showToast(d.error || "Failed", "error"); return; }
      await fetchQr2Url(); // Refresh QR URL from backend with new secret
      showToast("QR 2 rotated! Staff dashboard auto-updates.", "success");
    } catch { showToast("Network error", "error"); }
    finally { setRotating(false); }
  }

  async function handleSavePin() {
    if (!/^\d{4,6}$/.test(staffPin)) {
      showToast("PIN must be 4-6 digits", "error"); return;
    }
    setSavingPin(true);
    try {
      const r = await fetch(`${API}/rewards/admin/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, updates: { staffPin } }),
      });
      if (r.ok) { showToast(`Staff PIN updated to ${staffPin} ✅`, "success"); setStaffPin(""); }
      else { const d = await r.json(); showToast(d.error || "Failed", "error"); }
    } catch { showToast("Network error", "error"); }
    finally { setSavingPin(false); }
  }

  async function handleToggleTestMode(val) {
    setSavingTestMode(true);
    try {
      const r = await fetch(`${API}/rewards/admin/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, updates: { testMode: val } }),
      });
      if (r.ok) {
        setTestMode(val);
        localStorage.setItem(`testmode_${businessId}`, val ? "true" : "false");
        showToast(val ? "Test mode ON — GPS bypassed" : "Test mode OFF — GPS enforced at shop", val ? "info" : "success");
      }
    } catch { showToast("Network error", "error"); }
    finally { setSavingTestMode(false); }
  }

  async function handlePostOffer() {
    if (!offerText.trim()) { showToast("Enter offer text", "error"); return; }
    setPostingOffer(true);
    try {
      // Calculate expiresAt based on selection
      const now = new Date();
      let expiresAt;
      if (offerExpiry === "today") {
        // End of today at midnight IST
        const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        ist.setHours(23, 59, 59, 0);
        expiresAt = ist.toISOString();
      } else if (offerExpiry === "hours") {
        expiresAt = new Date(now.getTime() + parseInt(offerCustomHours) * 60 * 60 * 1000).toISOString();
      } else if (offerExpiry === "tomorrow") {
        const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        ist.setDate(ist.getDate() + 1);
        ist.setHours(23, 59, 59, 0);
        expiresAt = ist.toISOString();
      } else if (offerExpiry === "week") {
        expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      }
      const r = await fetch(`${API}/rewards/admin/offer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId,
          offer: { text: offerText.trim(), hindiText: offerHindi.trim() || null, expiresAt },
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setActiveOffer(d.offer);
        setOfferText(""); setOfferHindi("");
        showToast("Offer posted! Customers will see it now.", "success");
      } else showToast(d.error || "Failed", "error");
    } catch { showToast("Network error", "error"); }
    finally { setPostingOffer(false); }
  }

  async function handleSendPush(title, body) {
    setNotifying(true);
    try {
      const r = await fetch(`${API}/rewards/admin/push/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId,
          title: title || "Milkas Dairy 🥛",
          body: body || activeOffer?.text || "New special offer waiting for you!",
          url: "https://qkard.app/rewards/milkas",
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setPushCount(d.sent);
        showToast(`Notification sent to ${d.sent} customer${d.sent !== 1 ? "s" : ""} 🔔`, "success");
      } else showToast(d.error || "Failed to send", "error");
    } catch { showToast("Network error", "error"); }
    finally { setNotifying(false); }
  }

  async function handleClearOffer() {
    try {
      const r = await fetch(`${API}/rewards/admin/offer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, clear: true }),
      });
      if (r.ok) { setActiveOffer(null); showToast("Offer cleared", "success"); }
    } catch { showToast("Network error", "error"); }
  }

  async function handleBlock(memberId, action, reason) {
    setBlocking(memberId);
    try {
      const r = await fetch(`${API}/rewards/admin/block`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, memberId, action, reason }),
      });
      const d = await r.json();
      if (r.ok) {
        showToast(`Member ${action === "block" ? "blocked" : "unblocked"} ✅`, action === "block" ? "error" : "success");
        fetchAll();
      } else showToast(d.error || "Failed", "error");
    } catch { showToast("Network error", "error"); }
    finally { setBlocking(null); }
  }

  async function handleAdjust() {
    if (!adjustPhone || !adjustPoints || !adjustReason) { showToast("Fill all fields", "error"); return; }
    const pts = parseInt(adjustPoints);
    if (isNaN(pts)) { showToast("Invalid points", "error"); return; }
    setAdjusting(true);
    try {
      const phone = adjustPhone.startsWith("+91") ? adjustPhone : `+91${adjustPhone}`;
      const r = await fetch(`${API}/rewards/admin/adjust`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessId, phone, points: pts, reason: adjustReason }),
      });
      const d = await r.json();
      if (r.ok) {
        showToast(`${d.memberName}: ${d.newBalance} pts`, "success");
        setAdjustPhone(""); setAdjustPoints(""); setAdjustReason("");
        fetchAll();
      } else showToast(d.error || "Failed", "error");
    } catch { showToast("Network error", "error"); }
    finally { setAdjusting(false); }
  }

  function showToast(msg, type = "info") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  const totalPoints = members.reduce((s, m) => s + (m.points || 0), 0);
  const todayRedemptions = redemptions.filter(r => r.createdAt?.slice(0, 10) === todayIST()).length;

  const tabStyle = (t) => ({
    padding: "8px 14px", borderRadius: 8, border: "none",
    cursor: "pointer", fontFamily: "inherit", fontSize: 12,
    fontWeight: 700, whiteSpace: "nowrap",
    background: activeTab === t ? C.deepBlue : "transparent",
    color: activeTab === t ? C.white : C.textMid,
    transition: "all 0.15s",
  });

  const card = {
    background: C.white, borderRadius: 14, padding: 20,
    border: `1.5px solid ${C.border}`, marginBottom: 16,
  };

  const inputStyle = {
    width: "100%", padding: "11px 14px",
    border: `2px solid ${C.border}`, borderRadius: 10,
    fontSize: 14, fontWeight: 600, fontFamily: "inherit",
    color: C.textDark, background: C.cream, outline: "none",
    boxSizing: "border-box",
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: C.textMid, fontWeight: 600 }}>
      Loading…
    </div>
  );

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", paddingBottom: 40 }}>

      {/* Tabs */}
      <div style={{
        display: "flex", gap: 4, padding: "16px 0 12px",
        borderBottom: `2px solid ${C.border}`, marginBottom: 20, overflowX: "auto",
      }}>
        {[
          { id: "overview",    label: "📊 Overview" },
          { id: "offer",       label: "🎉 Today's Offer" },
          { id: "qr_staff",   label: "🔑 QR & Staff" },
          { id: "members",     label: "👥 Members" },
          { id: "redemptions", label: "📋 Redemptions" },
          { id: "adjust",      label: "⚙️ Adjust" },
          { id: "suspects",    label: "🚨 Suspects" },
        ].map(t => (
          <button key={t.id} style={tabStyle(t.id)} onClick={() => { setActiveTab(t.id); if(t.id==="qr_staff") fetchQr2Url(); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === "overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Total Members",      value: members.length,               icon: "👥" },
              { label: "Points Issued",       value: totalPoints.toLocaleString(), icon: "⭐" },
              { label: "Total Redemptions",   value: redemptions.length,           icon: "🎁" },
              { label: "Today's Redemptions", value: todayRedemptions,             icon: "📅" },
            ].map(s => (
              <div key={s.label} style={{ background: C.white, borderRadius: 14, padding: "16px 18px", border: `1.5px solid ${C.border}` }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: C.deepBlue }}>{s.value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: 1, textTransform: "uppercase", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>
            Top Members
          </div>
          {members.length === 0
            ? <div style={{ textAlign: "center", padding: 32, color: C.textMid, fontWeight: 600 }}>No members yet — share your QR poster!</div>
            : members.slice(0, 5).map((m, i) => (
              <div key={m.memberId} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${C.border}`, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: i === 0 ? C.saffron : C.lightBlue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: i === 0 ? C.deepBlue : C.midBlue }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>{m.phone} · {m.customerType}</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 900, color: C.midBlue }}>{m.points} pts</div>
              </div>
            ))
          }
        </div>
      )}

      {/* ── TODAY'S OFFER ── */}
      {activeTab === "offer" && (
        <div>
          {/* Active offer display */}
          {activeOffer && (
            <div style={{ background:"#1a3a5c", borderRadius:14, padding:20, marginBottom:16, position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", top:-20, right:-20, width:80, height:80, background:"rgba(245,166,35,0.15)", borderRadius:"50%" }} />
              <div style={{ fontSize:10, fontWeight:800, color:"#f5a623", letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Active Offer</div>
              <div style={{ fontSize:16, fontWeight:800, color:"#ffffff", marginBottom:4 }}>{activeOffer.text}</div>
              {activeOffer.hindiText && <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600, marginBottom:8 }}>{activeOffer.hindiText}</div>}
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontWeight:600, marginBottom:14 }}>
                Expires: {new Date(activeOffer.expiresAt).toLocaleString("en-IN", { timeZone:"Asia/Kolkata", dateStyle:"short", timeStyle:"short" })}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => handleSendPush("Milkas Dairy 🥛", activeOffer.text)}
                  disabled={notifying}
                  style={{ flex:1, background:"rgba(245,166,35,0.2)", border:"1px solid rgba(245,166,35,0.4)", borderRadius:10, padding:"8px 16px", color:"#f5a623", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {notifying ? "Sending…" : "🔔 Notify Members"}
                </button>
                <button onClick={handleClearOffer} style={{ flex:1, background:"rgba(220,38,38,0.2)", border:"1px solid rgba(220,38,38,0.4)", borderRadius:10, padding:"8px 16px", color:"#fca5a5", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  🗑 Clear Offer
                </button>
              </div>
            </div>
          )}

          {/* Post new offer */}
          <div style={{ background:C.white, borderRadius:14, padding:20, border:`1.5px solid ${C.border}` }}>
            <div style={{ fontSize:14, fontWeight:800, color:C.textDark, marginBottom:4 }}>Post Today's Special</div>
            <div style={{ fontSize:12, color:C.textMid, fontWeight:600, marginBottom:16 }}>Customers see this banner on the rewards page instantly.</div>

            <div style={{ fontSize:11, fontWeight:800, color:C.textMid, letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Offer Text (English)</div>
            <input value={offerText} onChange={e=>setOfferText(e.target.value)}
              placeholder="e.g. Double points today until 4 PM!"
              style={{ width:"100%", padding:"11px 14px", border:`2px solid ${C.border}`, borderRadius:10, fontSize:14, fontWeight:600, fontFamily:"inherit", color:C.textDark, background:C.cream, outline:"none", boxSizing:"border-box", marginBottom:12 }} />

            <div style={{ fontSize:11, fontWeight:800, color:C.textMid, letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Hindi Text (optional)</div>
            <input value={offerHindi} onChange={e=>setOfferHindi(e.target.value)}
              placeholder="e.g. आज 4 बजे तक double points!"
              style={{ width:"100%", padding:"11px 14px", border:`2px solid ${C.border}`, borderRadius:10, fontSize:14, fontWeight:600, fontFamily:"inherit", color:C.textDark, background:C.cream, outline:"none", boxSizing:"border-box", marginBottom:16 }} />

            <div style={{ fontSize:11, fontWeight:800, color:C.textMid, letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Valid For</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
              {[
                { val:"today", label:"Today only" },
                { val:"hours", label:"Custom hours" },
                { val:"tomorrow", label:"Until tomorrow" },
                { val:"week", label:"This week" },
              ].map(opt => (
                <button key={opt.val} onClick={()=>setOfferExpiry(opt.val)}
                  style={{ padding:"8px 14px", borderRadius:20, border:`1.5px solid ${offerExpiry===opt.val ? C.midBlue : C.border}`, background:offerExpiry===opt.val ? C.lightBlue : "transparent", color:offerExpiry===opt.val ? C.midBlue : C.textMid, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {offerExpiry === "hours" && (
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <span style={{ fontSize:13, color:C.textMid, fontWeight:600 }}>Expire after</span>
                <input type="number" min="1" max="72" value={offerCustomHours} onChange={e=>setOfferCustomHours(e.target.value)}
                  style={{ width:70, padding:"8px 12px", border:`2px solid ${C.border}`, borderRadius:10, fontSize:14, fontWeight:700, fontFamily:"inherit", color:C.deepBlue, background:C.cream, outline:"none", textAlign:"center" }} />
                <span style={{ fontSize:13, color:C.textMid, fontWeight:600 }}>hours</span>
              </div>
            )}

            <button onClick={handlePostOffer} disabled={postingOffer || !offerText.trim()}
              style={{ width:"100%", padding:13, background:offerText.trim() ? C.deepBlue : C.border, color:C.white, border:"none", borderRadius:10, fontSize:14, fontWeight:800, fontFamily:"inherit", cursor:offerText.trim() ? "pointer" : "not-allowed", marginBottom:10 }}>
              {postingOffer ? "Posting…" : "🎉 Post Offer Now"}
            </button>

            {/* Send push after posting */}
            {activeOffer && (
              <button onClick={() => handleSendPush("Milkas Dairy 🥛", activeOffer.text)}
                disabled={notifying}
                style={{ width:"100%", padding:12, background:"none", border:`2px solid ${C.midBlue}`, borderRadius:10, color:C.midBlue, fontSize:13, fontWeight:800, fontFamily:"inherit", cursor:"pointer" }}>
                {notifying ? "Sending…" : `🔔 Notify Members ${pushCount !== null ? `(${pushCount} sent)` : ""}`}
              </button>
            )}
          </div>

          <div style={{ marginTop:14, background:C.lightBlue, borderRadius:12, padding:"14px 16px", fontSize:13, fontWeight:700, color:C.deepBlue }}>
            💡 Ideas: Double points on slow days · Free lassi with paneer · Eid/Diwali special · Announce new product arrival
          </div>
        </div>
      )}

      {/* ── QR & STAFF ── */}
      {activeTab === "qr_staff" && (
        <div>

          {/* Staff page link */}
          <div style={{ ...card, background: C.deepBlue }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.white, marginBottom: 4 }}>Staff Dashboard</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 600, marginBottom: 14 }}>
              Share this link with your staff. They enter PIN → see QR 2 to show customers during redemption.
            </div>
            <div style={{
              background: "rgba(255,255,255,0.1)", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, fontWeight: 700,
              color: C.white, wordBreak: "break-all", marginBottom: 12,
            }}>
              qkard.app/staff/milkas
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => navigator.clipboard?.writeText("https://qkard.app/staff/milkas")}
                style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 10, color: C.white, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
              >
                📋 Copy Link
              </button>
              <button
                onClick={() => window.open(`https://wa.me/?text=Staff dashboard: https://qkard.app/staff/milkas%0APIN: (share separately)`, "_blank")}
                style={{ flex: 1, padding: "10px", background: "#25D366", border: "none", borderRadius: 10, color: C.white, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
              >
                📱 Share on WhatsApp
              </button>
            </div>
          </div>

          {/* Staff PIN setter */}
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark, marginBottom: 4 }}>Staff PIN</div>
            <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600, marginBottom: 14 }}>
              Current PIN: <strong>1234</strong> (seeded). Change it here if staff changes.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <input
                value={staffPin}
                onChange={e => setStaffPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="New PIN (4-6 digits)"
                type="tel" maxLength={6}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={handleSavePin}
                disabled={savingPin || staffPin.length < 4}
                style={{
                  padding: "11px 18px", background: staffPin.length >= 4 ? C.midBlue : C.border,
                  color: C.white, border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 800, fontFamily: "inherit",
                  cursor: staffPin.length >= 4 ? "pointer" : "not-allowed",
                }}
              >
                {savingPin ? "…" : "Save"}
              </button>
            </div>
          </div>

          {/* Test mode toggle */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark, marginBottom: 2 }}>Test Mode</div>
                <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>
                  {testMode ? "ON — GPS check bypassed (for testing from home)" : "OFF — GPS enforced at shop (production)"}
                </div>
              </div>
              <button
                onClick={() => handleToggleTestMode(!testMode)}
                disabled={savingTestMode}
                style={{
                  padding: "8px 16px", borderRadius: 20, border: "none",
                  background: testMode ? C.saffron : C.success,
                  color: testMode ? C.deepBlue : C.white,
                  fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  flexShrink: 0, marginLeft: 12,
                }}
              >
                {savingTestMode ? "…" : testMode ? "Turn OFF" : "Turn ON"}
              </button>
            </div>
            {testMode && (
              <div style={{ marginTop: 12, background: "#fffbf0", borderRadius: 10, padding: "10px 12px", fontSize: 12, fontWeight: 700, color: C.saffron }}>
                ⚠️ Remember to turn OFF test mode before going live at Milkas shop.
              </div>
            )}
          </div>

          {/* QR2 preview + rotation */}
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark, marginBottom: 4 }}>QR 2 — Current Redemption QR</div>
            <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600, marginBottom: 14 }}>
              Auto-rotates daily at 6 AM. Staff dashboard always shows today's QR. Only force-rotate if you suspect fraud.
            </div>

            {/* QR preview */}
            {qr2Url ? (
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&ecc=M&data=${encodeURIComponent(qr2Url)}`}
                  alt="QR 2 Preview"
                  style={{ width: 160, height: 160, borderRadius: 12, border: `2px solid ${C.border}`, display: "block", margin: "0 auto 8px" }}
                  onError={e => { e.target.style.display = "none"; }}
                />
                <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600 }}>Current QR 2 — same as staff dashboard</div>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 0", marginBottom: 16 }}>
                <button onClick={fetchQr2Url} style={{ background: C.lightBlue, border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 700, color: C.midBlue, cursor: "pointer", fontFamily: "inherit" }}>
                  Load QR Preview
                </button>
              </div>
            )}
            <button
              onClick={handleRotateQR2}
              disabled={rotating}
              style={{
                width: "100%", padding: 12, background: "none",
                border: `2px solid ${C.error}`, borderRadius: 10,
                color: C.error, fontSize: 13, fontWeight: 800,
                cursor: rotating ? "not-allowed" : "pointer", fontFamily: "inherit",
              }}
            >
              {rotating ? "Rotating…" : "🔄 Force Rotate QR 2 Now (Security Only)"}
            </button>
            <div style={{ marginTop: 12, background: "#fef2f2", borderRadius: 10, padding: "10px 12px", fontSize: 12, fontWeight: 700, color: C.error }}>
              ⚠️ Only use this if you suspect QR 2 was photographed and shared outside the shop.
            </div>
          </div>
        </div>
      )}

      {/* ── MEMBERS ── */}
      {activeTab === "members" && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>
            {members.length} Members (Sorted by Points)
          </div>
          {members.length === 0
            ? <div style={{ textAlign: "center", padding: 40, color: C.textMid, fontWeight: 600 }}>No members yet.</div>
            : members.map(m => (
              <div key={m.memberId} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${C.border}`, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.lightBlue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: C.midBlue }}>
                    {m.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>{m.phone} · {m.customerType} · joined {m.joinedAt?.slice(0, 10)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: C.midBlue }}>{m.points}</div>
                    <div style={{ fontSize: 10, color: C.textLight, fontWeight: 600 }}>pts</div>
                  </div>
                </div>
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                  {m.visitStreak > 1 && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(245,166,35,0.1)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: C.saffron }}>
                      🔥 {m.visitStreak} day streak
                    </div>
                  )}
                  {m.status === "BLOCKED" && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fef2f2", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: C.error }}>
                      🚫 Blocked
                    </div>
                  )}
                  <button
                    onClick={() => {
                      const action = m.status === "BLOCKED" ? "unblock" : "block";
                      const reason = action === "block" ? window.prompt(`Reason for blocking ${m.name}?`) : "Owner unblocked";
                      if (action === "block" && !reason) return;
                      handleBlock(m.memberId, action, reason);
                    }}
                    disabled={blocking === m.memberId}
                    style={{
                      marginLeft: "auto", padding: "4px 12px",
                      background: m.status === "BLOCKED" ? "#dcfce7" : "#fef2f2",
                      border: `1px solid ${m.status === "BLOCKED" ? C.success : C.error}`,
                      borderRadius: 20, fontSize: 11, fontWeight: 700,
                      color: m.status === "BLOCKED" ? C.success : C.error,
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {blocking === m.memberId ? "…" : m.status === "BLOCKED" ? "✅ Unblock" : "🚫 Block"}
                  </button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* ── REDEMPTIONS ── */}
      {activeTab === "redemptions" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 2, textTransform: "uppercase" }}>Recent Redemptions</div>
            <button onClick={fetchAll} style={{ background: "none", border: `1.5px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: C.textMid, cursor: "pointer", fontFamily: "inherit" }}>↻ Refresh</button>
          </div>
          {redemptions.length === 0
            ? <div style={{ textAlign: "center", padding: 40, color: C.textMid, fontWeight: 600 }}>No redemptions yet.</div>
            : redemptions.map((r, i) => (
              <div key={i} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${C.border}`, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 24 }}>🎁</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark }}>{r.meta?.rewardName || "Reward"}</div>
                  <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>Code: {r.meta?.confirmationCode} · {timeAgo(r.createdAt)}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, color: C.error }}>{r.points} pts</div>
              </div>
            ))
          }
        </div>
      )}

      {/* ── ADJUST ── */}
      {activeTab === "adjust" && (
        <div>
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark, marginBottom: 4 }}>Manual Point Adjustment</div>
            <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600, marginBottom: 20 }}>
              For edge cases only — customer missed scan, correction, goodwill bonus.
            </div>
            {[
              { label: "Customer Phone", value: adjustPhone, set: setAdjustPhone, placeholder: "10-digit mobile", type: "tel" },
              { label: "Points (+add / −deduct)", value: adjustPoints, set: setAdjustPoints, placeholder: "e.g. 20 or -10", type: "text" },
              { label: "Reason", value: adjustReason, set: setAdjustReason, placeholder: "e.g. Missed scan, Goodwill, Diwali bonus", type: "text" },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{f.label}</div>
                <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} type={f.type} style={inputStyle} />
              </div>
            ))}
            <button onClick={handleAdjust} disabled={adjusting} style={{ width: "100%", padding: 13, background: C.deepBlue, color: C.white, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, fontFamily: "inherit", cursor: "pointer", opacity: adjusting ? 0.7 : 1 }}>
              {adjusting ? "Applying…" : "Apply Adjustment"}
            </button>
          </div>
          <div style={{ background: C.lightBlue, borderRadius: 12, padding: "14px 16px", fontSize: 13, fontWeight: 700, color: C.deepBlue }}>
            ⚠️ All adjustments are permanently logged in the audit trail.
          </div>
        </div>
      )}

      {/* ── SUSPECTS ── */}
      {activeTab === "suspects" && (
        <div>
          <div style={{ background: "#fef2f2", borderRadius: 12, padding: "14px 16px", marginBottom: 16, fontSize: 13, fontWeight: 700, color: C.error }}>
            🚨 {suspects.length} suspicious account{suspects.length !== 1 ? "s" : ""} detected automatically.
          </div>
          {suspects.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.textMid, fontWeight: 600 }}>
              ✅ No suspicious activity detected.
            </div>
          )}
          {suspects.map(m => (
            <div key={m.memberId} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${C.error}`, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                  🚨
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>{m.phone}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: C.midBlue }}>{m.points} pts</div>
                </div>
              </div>
              {/* Flags */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                {(m.computedFlags||[]).map(flag => (
                  <div key={flag} style={{ background: "#fef2f2", border: `1px solid ${C.error}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: C.error }}>
                    {{
                      "REFERRAL_FARMER": "🌾 Referral Farmer",
                      "HIGH_VISITS_NO_REDEEM": "📍 High Visits, No Redeem",
                      "REDEEMED_NO_POINTS": "🎁 Redeemed Without Points",
                      "BLOCKED": "🚫 Already Blocked",
                    }[flag] || flag}
                  </div>
                ))}
              </div>
              {/* Stats */}
              <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 12, color: C.textMid, fontWeight: 600 }}>
                <span>👁 {m.visitCount || 0} visits</span>
                <span>🎁 {m.referralsRewarded || 0} referrals rewarded</span>
                <span>📅 Joined {m.joinedAt?.slice(0,10)}</span>
              </div>
              {/* Actions */}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => {
                    const reason = m.status === "BLOCKED" ? "Owner unblocked" : window.prompt(`Reason for blocking ${m.name}?`);
                    if (m.status !== "BLOCKED" && !reason) return;
                    handleBlock(m.memberId, m.status === "BLOCKED" ? "unblock" : "block", reason);
                  }}
                  disabled={blocking === m.memberId}
                  style={{
                    flex: 1, padding: "10px",
                    background: m.status === "BLOCKED" ? "#dcfce7" : C.error,
                    border: "none", borderRadius: 10,
                    color: m.status === "BLOCKED" ? C.success : C.white,
                    fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  {blocking === m.memberId ? "…" : m.status === "BLOCKED" ? "✅ Unblock Member" : "🚫 Block Member"}
                </button>
              </div>
            </div>
          ))}
          <div style={{ background: C.lightBlue, borderRadius: 12, padding: "14px 16px", fontSize: 13, fontWeight: 700, color: C.deepBlue, marginTop: 8 }}>
            💡 Blocking a member immediately stops all earning and redemption. They see a suspension message. You can unblock anytime.
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "success" ? C.success : toast.type === "error" ? C.error : C.deepBlue,
          color: C.white, padding: "12px 24px", borderRadius: 30,
          fontSize: 13, fontWeight: 700, boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          zIndex: 999, whiteSpace: "nowrap",
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
